export function dataURLtoBlob(dataURL: string): Blob {
  const [header, b64] = dataURL.split(",");
  const mime = header.match(/:(.*?);/)![1];
  const binary = atob(b64);
  const len = binary.length;
  const u8 = new Uint8Array(len);
  for (let j = 0; j < len; j++) u8[j] = binary.charCodeAt(j);
  return new Blob([u8], { type: mime });
}

export async function generateVideoThumbnails(
  urlOrFile: string | File,
  count = 1,
  maxSizeKB = 15,
  maxWidth = 320
): Promise<string[]> {
  const targetBytes = maxSizeKB * 1024;
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.preload = "auto";
  video.muted = true;
  video.playsInline = true;
  video.src =
    typeof urlOrFile === "string"
      ? urlOrFile
      : URL.createObjectURL(urlOrFile);

  await new Promise<void>((res, rej) => {
    video.addEventListener("loadedmetadata", () => res(), { once: true });
    video.addEventListener("error", () => rej(new Error("Video load failed")), {
      once: true,
    });
    video.load();
  });

  if (video.duration < 2) {
    if (typeof urlOrFile !== "string") URL.revokeObjectURL(video.src);
    throw new Error("Video too short (must be >2s)");
  }

  const interval = (video.duration - 2) / count;
  const captureTimes = Array.from(
    { length: count },
    (_, i) => 1 + i * interval
  );

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas not supported");
  }

  const thumbs: string[] = [];
  const toJpegBlob = (q: number) =>
    new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Thumbnail encode failed"))),
        "image/jpeg",
        q
      )
    );

  for (const t of captureTimes) {
    await new Promise<void>((res) => {
      const onSeek = () => {
        video.removeEventListener("seeked", onSeek);
        res();
      };
      video.addEventListener("seeked", onSeek);
      video.currentTime = t;
    });

    const scale = Math.min(1, maxWidth / video.videoWidth);
    canvas.width = Math.floor(video.videoWidth * scale);
    canvas.height = Math.floor(video.videoHeight * scale);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    let low = 0;
    let high = 1;
    let bestBlob = await toJpegBlob(high);

    for (let i = 0; i < 8; i++) {
      const mid = (low + high) / 2;
      const blob = await toJpegBlob(mid);
      if (blob.size > targetBytes) {
        high = mid;
      } else {
        low = mid;
        bestBlob = blob;
      }
    }

    if (bestBlob.size > targetBytes) {
      let currW = canvas.width;
      while (bestBlob.size > targetBytes && currW > 50) {
        currW = Math.floor(currW * 0.8);
        const factor = currW / video.videoWidth;
        canvas.width = currW;
        canvas.height = Math.floor(video.videoHeight * factor);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        bestBlob = await toJpegBlob(low);
      }
    }

    const dataUrl = await new Promise<string>((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result as string);
      reader.onerror = () => rej(new Error("FileReader error"));
      reader.readAsDataURL(bestBlob);
    });

    thumbs.push(dataUrl);
  }

  if (typeof urlOrFile !== "string") URL.revokeObjectURL(video.src);
  return thumbs;
}
